import { responseGoogle, userActions, user_profileActions } from "actions";
import { CONTENT_GROUP_ACTION, userConstants } from "../../../constants";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useUserChannel from "./useUserChannel";

const INTERVAL_MS = 30000;

// Reshape the flat statistics `active_card` payload into the same structure the
// current user's tracked_card has, so the derivations below (workspace_logo,
// onClick, name) work regardless of which user is being tracked.
const normalizeActiveCard = (activeCard) => {
    if (!activeCard) return null;

    return {
        id: activeCard.id,
        name: activeCard.name,
        workspace_id: activeCard.workspace_id,
        stage_id: activeCard.stage_id,
        timestamp_info: { latest_start: activeCard.tracking_started_at },
        workspace: {
            id: activeCard.workspace_id,
            name: activeCard.workspace_name,
            logo: activeCard.thumbnail_url ? { thumb_url: activeCard.thumbnail_url } : null,
        },
    };
};

// Tracks the card a user is currently working on. With no `userId` it tracks the
// current (logged-in) user via redux + the action-cable `connected` poll (the
// stopwatch button). With a `userId` it tracks that profile user's active card
// via the statistics endpoint, kept in local state so it doesn't clobber the
// current user's tracked_card in redux.
const useTimestampButton = ({ connected, userId, excludedWorkspaceIds = [], excludedOrganizationIds = [] } = {}) => {
    const dispatch = useDispatch();
    const isProfile = Boolean(userId);

    // Stable primitive keys so the effect/callbacks below re-run when the
    // workspace filter actually changes, not on every render's new array ref.
    const excludedWorkspaceKey = excludedWorkspaceIds.join(",");
    const excludedOrganizationKey = excludedOrganizationIds.join(",");

    // Self: card lives in redux. Profile: local state + loading for the tile.
    const { tracked_card: selfTrackedCard } = useSelector(state => state.users) || {};
    const [profileCard, setProfileCard] = useState(null);
    const [loading, setLoading] = useState(isProfile);

    const tracked_card = isProfile ? profileCard : selfTrackedCard;
    const { timestamp_info, stage, workspace } = tracked_card || {};
    const isActive = (stage?.enable_timestamp && !stage?.active) ? true : false;
    const isHidden = !tracked_card;

    const workspace_logo = useMemo(() => {
        if (workspace && workspace?.logo) {
            return workspace?.logo?.thumb_url;
        }
    }, [workspace]);

    const onClick = useCallback(() => {
        if (!tracked_card && !workspace) return;

        dispatch({ type: CONTENT_GROUP_ACTION.CLOSE_CARD_MODAL });
        setTimeout(() => {
            dispatch({ type: CONTENT_GROUP_ACTION.OPEN_CARD_MODAL, payload: { id: tracked_card?.id, workspace_id: workspace?.id } });
        }, 500);
    }, [tracked_card, workspace]);

    const fetchProfileActiveCard = useCallback(() => (
        user_profileActions
            .fetchActiveCard(userId, excludedWorkspaceIds, excludedOrganizationIds)
            .then((data) => (data && data.status === "ok" ? normalizeActiveCard(data.active_card) : null))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [userId, excludedWorkspaceKey, excludedOrganizationKey]);

    const handleRealtimeUpdate = useCallback(() => {
        fetchProfileActiveCard()
            .then((card) => setProfileCard(card))
            .catch(() => setProfileCard(null))
            .finally(() => setLoading(false));
    }, [fetchProfileActiveCard]);

    useUserChannel({
        userId: isProfile ? userId : null,
        onUpdate: handleRealtimeUpdate,
    });

    useEffect(() => {
        let cancelled = false;

        const fetchSelf = () => {
            const onSuccess = (response) => {
                dispatch({ type: userConstants.TRACKED_CARD_ADD, payload: response.data.tracked_card });
            };
            const onError = () => dispatch({ type: userConstants.TRACKED_CARD_ADD, payload: null });

            dispatch(userActions.getTrackedCard({ onSuccess, onError, onFailure: onError }));
        };

        const fetchProfile = () => {
            fetchProfileActiveCard()
                .then((card) => { if (!cancelled) setProfileCard(card); })
                .catch(() => { if (!cancelled) setProfileCard(null); })
                .finally(() => { if (!cancelled) setLoading(false); });
        };

        const fetchData = isProfile ? fetchProfile : fetchSelf;

        if (isProfile || connected) {
            fetchData();
        }

        const interval = setInterval(fetchData, INTERVAL_MS);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [connected, userId, excludedWorkspaceKey, excludedOrganizationKey]);

    return {
        workspace_logo,
        isActive,
        timestamp_info, // {latest_change_total_seconds, latest_start}
        isHidden,
        loading,
        tracked_card,
        onClick,
    };
};

export default useTimestampButton;
